import { AnchorType } from '../../constants';
import { groupIds } from '../../shapes/constants';
import { AnchorStyle, Connector, AnchorTuple, NodeModel, DrawOpts } from '../../types';

export const getAnchorGroupId = (anchorType: AnchorType, index: number): string =>
  `${groupIds.anchor}-${anchorType}-${index}`;

export const getAnchorTypeStyle = (
  anchorType: AnchorType,
  anchorStyle: AnchorStyle,
): AnchorStyle => {
  const isInAnchor = anchorType === 'in';

  return {
    ...anchorStyle,
    // Because the top anchor isn't shown
    fill: isInAnchor ? 'transparent' : anchorStyle.fill,
    stroke: isInAnchor ? 'transparent' : anchorStyle.stroke,
    cursor: isInAnchor ? 'default' : 'pointer',
  };
};

/**
 * Gets the minimum width the node needs to be to fit all the anchors
 */
export const getWidthNeededForAnchors = (numOutputConnectors = 0, opts: DrawOpts): number => {
  if (!opts) return 0;

  const { size = [0, 0], options } = opts;
  // NOTE: This currently only supports the [width, height] size format
  // TODO: configure alternative sizing
  const [width] = size;

  if (numOutputConnectors === 0) return width;

  const { maxWidth = 0 } = options[groupIds.anchor];

  return maxWidth * numOutputConnectors;
};

/**
 * Alters anchors x coords so that they're spread evenly through a node
 */
export const parseAnchors = (connectors: Connector[] = [], yCoord = 0): AnchorTuple[] => {
  // We're doing double the connectors to give the anchor the illusion of being
  // in the middle of the width
  const gap = 1 / (connectors.length * 2);

  return connectors.map((c, i) => {
    /**
     * The anchor should be at odd gaps.
     *
     * EG:
     * Let say we have 3 anchors.  We'd want the node to look like the following:
     *
     *       0           1           2        <-- anchor indexes
     *       |           |           |
     * |-----|-----|-----|-----|-----|-----|
     *  _____ _____ _____ _____ _____ _____
     *    |     |     |     |     |     |
     *    1     2     3     4     5     6     <-- gaps
     *
     *
     * This means each gap is 1/6, and the anchors need to appear at the end of
     * gap 1, gap 3 and gap 5
     *
     * Therefore, we'd want the x ratio for the indexes to be as follows:
     * - 0: 1/6 (IE: gap)
     * - 1: 3/6 (IE: gap + 2/6)
     * - 2: 5/6 (IE: gap + 4/6)
     *
     * This gives the illusion of the anchors being at the middle of a set area,
     * which coordinates to the designs.
     */
    return [gap + gap * (i * 2), yCoord, c.name];
  });
};

interface GetAnchorPointsResp {
  inputs: AnchorTuple[];
  outputs: AnchorTuple[];
  all: AnchorTuple[];
}

/**
 * Converts the anchor points from the model into ones understandable by G6
 * G6 an array of *all* anchor points
 * We later seperate this into input/output
 */
export const getAnchorPoints = (model: NodeModel): GetAnchorPointsResp => {
  const { inputConnectors = [], outputConnectors = [] } = model;
  const inputs = parseAnchors(inputConnectors, 0);
  const outputs = parseAnchors(outputConnectors, 1);

  return {
    inputs,
    outputs,
    all: [...inputs, ...outputs],
  };
};
